/**
 * What a visitor is allowed to see, decided from `me` alone.
 *
 * The guard split (`components/guards.tsx`) shipped the one defect this whole
 * migration produced: `RequireAccount` redirecting to a combined answer sent
 * a learner with no character to `/character` from inside a guard that also
 * wraps `/character`, a permanent dead end (`af9010c`). Pulling the decision
 * out as a pure function makes *this* logic — signup vs. character vs. ok —
 * unit-testable without a Relay runtime or a `.tsx` loader, dependency-free
 * from Relay's generated types for the same reason `sessions.ts` is. It does
 * not cover the wiring that actually shipped the dead end: which guard nests
 * inside which in `routes.tsx` still has no test.
 */
export type GuardAccount =
  | {
      learner?: {
        defaultCharacter?: { id: string } | null;
      } | null;
    }
  | null
  | undefined;

export type GuardDecision = 'signup' | 'character' | 'ok';

/**
 * `"signup"` covers both a signed-out visitor (`me` is `null`) and a signed-in
 * account with no learner yet — `RequireAccount` sends both to the same
 * place, and `/signup` itself carries no guard for exactly that second case
 * (spec §4: redirecting it to `/` would loop, since `/` sits behind this same
 * check).
 */
export function guardFor(me: GuardAccount): GuardDecision {
  if (!me?.learner) return 'signup';
  if (!me.learner.defaultCharacter) return 'character';
  return 'ok';
}
